import { Children, FC, useState, useRef, useCallback, useEffect } from 'react';
import { SyncButton } from '../components/buttons';
import Table, { TextCell, ButtonCell } from '../components/Table';
import {Container, Dimmer, Loader, Popup, Segment} from 'semantic-ui-react';
import Pagination from '../components/Pagination';
import MessageWindow from '../components/MessageWindow';
import { api, useApi } from '../api/api';
import {ApiFileChunk} from '../api/io';
import {Event as ApiEvent, Event, EventFilter} from '../api/events';
import FileChunkWindow from "../components/FileChunkWindow";
import { envColor } from '../common';


const PAGE_SIZE = 15;


const Heartbeats: FC<{}> = () => {
    const eventsApi = useApi(api.events);
    const bucketApi = useApi(api.bucket)
    const [events, setEvents] = useState<Event[]>([]);
    const [loading, setLoading] = useState(false);
    const [activePage, setActivePageImpl] = useState<number>(1);
    const activePageRef = useRef<number>(activePage);
    const setActivePage = (newPage: number) => {
        activePageRef.current = newPage;
        setActivePageImpl(newPage);
    };
    const [totalPages, setTotalPages] = useState(1);
    const filter: EventFilter = [
        {field: 'source', operator: '=', value: 'sparrow/RCG/HeartBeatWebSite'}
    ];
    const [fileLoading, setFileLoading] = useState(false);
    const [currentFileChunk, setFileChunk] = useState<ApiFileChunk>();
    const [currentAlert, setAlert] = useState('');

    const load = async () => {
        setLoading(true);

        return eventsApi
            .index(filter, activePageRef.current, PAGE_SIZE)
            .then(({ items, count }) => {
                setEvents(items);
                setTotalPages(Math.ceil(count / PAGE_SIZE))
            })
            .catch(e => console.error(e))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load() }, [activePage]);

    const bucketDownload = (url: string, index: number = 1) => {
        if (url == null || url === "None") {
            setAlert("Requested resource doesn't exist");
        }
        else {
            setFileLoading(true);
            bucketApi
                .read_chunk(url, index)
                .then(setFileChunk)
                .catch(() => { setAlert(`Requested resource doesn't exist : "${url}"`); })
                .finally(() => setFileLoading(false))
        }
    };

    const urlDownload = (url: string) => {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', url);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
    };


    const getTableCells = useCallback(() => {
        const file = (header: string, value: (data: Event) => any) => (
            <ButtonCell {...{header, value}} icon='arrow down' onClick={bucketDownload} color={envColor} />
        );

        const text = (header: string, value: (data: Event) => any) => (
            <TextCell {...{header, value}} />
        );


        const link = (header: string, value: (data: Event) => any) => (
            <ButtonCell {...{header, value}} icon='arrow down' onClick={urlDownload} color={envColor} />
        );

        const textCellWithPopUpForFileUrl = (header: string, fileUrlValue: (data: ApiEvent) => any) => (
            <TextCell header={header} value={(e) => popupForFileUrl(fileUrlValue(e))}/>
        )

        const popupForFileUrl = (fileUrlValue: string) => {
            let popupValue = fileUrlValue.split('/').slice(-1)[0]
            let button = <ButtonCell style={{display: 'inline', background:'white'}} header='' value={() => fileUrlValue} icon='arrow down' onClick={bucketDownload} />
            if (popupValue==='None'){
                button = <ButtonCell
                    disabled
                    button={{ color: 'grey' }}
                    style={{display: 'inline', background:'white'}}
                    header='' value={() => ''}
                    icon='arrow down' />
            }
            return <Popup content={popupValue} trigger={button}/>
        }


        const definitions = {
            source_file:        textCellWithPopUpForFileUrl('input', e => e.source_file),
            download_start:     text('download_start', e => e.download_start),
            download_end:       text('download end', e => e.download_end),
            process_time:       text('process time', e => e.process_time),
            event_log:          textCellWithPopUpForFileUrl('event log', e => e.event_log),
            process_log:        textCellWithPopUpForFileUrl('process log', e => e.process_log),
            file_size:          text('file size', e => e.file_size),
        };

        return Children.toArray(Object.values(definitions));
    }, []);

    return (
        <>
            <Dimmer active={fileLoading}><Loader/></Dimmer>

            {currentFileChunk &&
                <FileChunkWindow
                    file={currentFileChunk}
                    onClose={() => setFileChunk(undefined)}
                    filePageChange={bucketDownload}
                    loading={fileLoading}
                    saveFile={urlDownload}
                />
            }

            {currentAlert.length > 0 &&
                <MessageWindow
                    size="small"
                    message={currentAlert}
                    onClose={() => setAlert('')}
                />
            }

            <Segment style={{ margin: '50px 100px 0 100px' }}>
                <Container fluid>
                    <SyncButton load={load} color={envColor} />

                    {loading ?
                        <Dimmer inverted active={true}><Loader/></Dimmer> :
                        <>
                            <Table
                                rows={events ?? []}
                            >
                                {getTableCells()}
                            </Table>
                            <Pagination
                                activePage={activePage}
                                totalPages={totalPages}
                                onPageChange={setActivePage}
                            />
                        </>
                    }
                </Container>
            </Segment>
        </>
    );
};


export default Heartbeats;